/**
 * The unified Tasks and Inbox surface (work order M6 Station 2a item 1).
 *
 * Provides:
 * 1. Tasks view listing the caller's open tasks from GET /api/tasks
 *    (title, context, age, the entity it points at as a navigable link)
 *    with a complete action where the task allows it.
 * 2. Notifications presented per the existing notification center pattern,
 *    extended, not duplicated.
 * 3. Contained scrollable register per UX_SPEC, skeleton loading, plain language only.
 * 4. Responsive to 390px.
 */

import { ListFilter } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ApiError } from "../api/client";
import {
  isAttended,
  isUnread,
  listNotifications,
  markNotificationRead,
  type Notification,
} from "../api/notifications";
import {
  completeTask,
  isTaskOpen,
  isTaskOverdue,
  isTaskRecordType,
  listTasks,
  type Task,
  type TaskRecord,
} from "../api/tasks";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { FyiBlock } from "../components/FyiBlock";
import { TaskDue } from "../components/TaskDue";
import { RegisterView } from "../components/RegisterView";
import { ToastBar } from "../nav/ActionDialog";
import { canonicalPath } from "../nav/deepLinks";
import { Breadcrumbs, PageSearch, Pagination, StateChip, day } from "./parts";
import "../brand/components.css";

export type TasksTab = "tasks" | "inbox";

export function formatTaskAge(createdAt: string): string {
  try {
    const created = new Date(createdAt).getTime();
    const now = Date.now();
    const diffMs = now - created;
    if (diffMs < 0) {
      return "Just now";
    }
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) {
      return "Just now";
    }
    if (diffHours < 24) {
      return "Today";
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) {
      return "1 day ago";
    }
    return `${String(diffDays)} days ago`;
  } catch {
    return "";
  }
}

/** A task kind in plain words; an unknown kind reads as words, never as a code. */
export function taskTitle(kind: string): string {
  const known = strings.tasks.kinds[kind];
  if (known !== undefined) {
    return known;
  }
  const lower = kind.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * The two front-door tasks open their dialog on the project page (M6 Station 2b): the
 * link carries `?act=`, and the project page opens the approval or assignment overlay.
 */
const PROJECT_ACT: Record<string, { act: string; label: string }> = {
  APPROVE_PROJECT: { act: "approve", label: "Review and approve" },
  ASSIGN_PERSONNEL: { act: "assign", label: "Assign personnel" },
  // The staffing backlog (Mahmoud's ruling 2026-10-11): the project's assign dialog.
  STAFF_PROJECT: { act: "assign", label: "Assign a QA/QC engineer" },
};

/** The record page a task's subject lives on, by subject type (wave W1 stations). */
function subjectPath(task: Task): { path: string; label: string } | null {
  switch (task.subjectType) {
    case "PROJECT":
      return { path: `/portfolio/projects/${task.subjectId}`, label: "View project" };
    case "CONTRACT":
      return { path: `/portfolio/contracts/${task.subjectId}`, label: "View contract" };
    case "ITP":
      return { path: `/itps/${task.subjectId}`, label: "View plan" };
    case "SCHEDULE":
      return { path: `/schedules/${task.subjectId}`, label: "View schedule" };
    case "FINDING":
      return { path: `/findings/${task.subjectId}`, label: "View finding" };
    // Wave COM item 2: the effectiveness review is raised on the closed action request.
    case "ACTION_REQUEST":
      return { path: `/actions/${task.subjectId}`, label: strings.tasks.viewActionRequest };
    // Wave COM item 2: an escalation's subject is the overdue task itself; the server
    // stores that task's own record as the deep link, so this is only the fallback.
    case "TASK":
      return { path: "/tasks", label: strings.tasks.viewOverdueRecord };
    default:
      return null;
  }
}

export function resolveEntityLink(task: Task): { path: string; label: string } {
  const projectAct = PROJECT_ACT[task.kind];
  if (projectAct !== undefined && task.subjectType === "PROJECT") {
    return {
      path: `/portfolio/projects/${task.subjectId}?act=${projectAct.act}`,
      label: projectAct.label,
    };
  }
  if (task.deepLink) {
    if (task.deepLink.startsWith("/projects/")) {
      return { path: `/portfolio${task.deepLink}`, label: "View project" };
    }
    if (task.deepLink.startsWith("/contracts/")) {
      return { path: `/portfolio${task.deepLink}`, label: "View contract" };
    }
    if (task.deepLink.startsWith("/parties/")) {
      return { path: "/portfolio/parties", label: "View party" };
    }
    const subject = subjectPath(task);
    return { path: canonicalPath(task.deepLink), label: subject?.label ?? "View entity" };
  }
  return subjectPath(task) ?? { path: "/tasks", label: "View task" };
}

/**
 * Where a task's ACT lives (wave W1, "Complete" becomes "Open the action"). A workflow
 * task closes by itself when its act happens, so the button takes the person to the
 * screen where the act is done: the project page's dialog for the front door, the
 * plans register's authoring form for the baton, and the record's own page for every
 * station after it (ITP review and revision, schedule signatures and acknowledgement,
 * the finding response loop).
 */
export function taskActionPath(task: Task): string {
  if (task.kind === "AUTHOR_ITPS") {
    return task.subjectId
      ? `/itps?act=create&contractId=${encodeURIComponent(task.subjectId)}`
      : "/itps?act=create";
  }
  const path = resolveEntityLink(task).path;
  // Wave INST ruling 4: the authority's task opens its dialog on the record (the RFI's
  // waiver or the action request's disposition), not only the record.
  if (task.kind === "AUTHORITY_REVIEW") {
    return `${path}${path.includes("?") ? "&" : "?"}act=authority`;
  }
  return path;
}

export function resolveNotificationLink(
  item: Notification,
): { path: string; label: string } | null {
  if (!item.deepLink) {
    return null;
  }
  if (item.deepLink.startsWith("/projects/")) {
    return { path: `/portfolio${item.deepLink}`, label: "View project" };
  }
  if (item.deepLink.startsWith("/contracts/")) {
    return { path: `/portfolio${item.deepLink}`, label: "View contract" };
  }
  if (item.deepLink.startsWith("/parties/")) {
    return { path: "/portfolio/parties", label: "View party" };
  }
  return { path: canonicalPath(item.deepLink), label: "Open link" };
}

const PAGE_SIZE = 25;

/** Wave COM item 1: the register's order and its due filter. */
export type TaskOrder = "newest" | "due";
export type TaskDueFilter = "any" | "overdue" | "dated";

/**
 * "Newest first" is the server's own order (created_at descending). "Due soonest" puts
 * the nearest due time first and a task without a due time after every dated one.
 */
export function orderTasks(tasks: readonly Task[], order: TaskOrder): Task[] {
  const newest = (a: Task, b: Task): number => b.createdAt.localeCompare(a.createdAt);
  if (order === "newest") {
    return [...tasks];
  }
  return [...tasks].sort((a, b) => {
    if (a.dueAt === null || b.dueAt === null) {
      return a.dueAt === b.dueAt ? newest(a, b) : a.dueAt === null ? 1 : -1;
    }
    const by = new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    return by !== 0 ? by : newest(a, b);
  });
}

export function keepsDue(task: Task, filter: TaskDueFilter): boolean {
  if (filter === "overdue") {
    return isTaskOverdue(task);
  }
  if (filter === "dated") {
    return task.dueAt !== null;
  }
  return true;
}

/**
 * The task list's link narrowed to one record (wave W2, Mahmoud's UAT: "View open
 * tasks" on CTR-18 led to a list carrying an ITP task of ANOTHER contract). The label
 * only names the record on the banner; the server does the narrowing.
 */
export function recordTasksPath(record: TaskRecord, label: string | null): string {
  const params = new URLSearchParams({ record_type: record.type, record_id: record.id });
  if (label !== null && label !== "") {
    params.set("record_label", label);
  }
  return `/tasks?${params.toString()}`;
}

/** The record the URL narrows the list to, or null for the whole list. */
function recordOf(type: string | null, id: string | null): TaskRecord | null {
  return isTaskRecordType(type) && id !== null && id !== "" ? { type, id } : null;
}

export function TasksPage(): React.JSX.Element {
  const token = useToken();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const recordType = params.get("record_type");
  const recordId = params.get("record_id");
  const record = useMemo(() => recordOf(recordType, recordId), [recordType, recordId]);
  const recordLabel = params.get("record_label") ?? strings.tasks.recordUnnamed;

  const [tab, setTab] = useState<TasksTab>("tasks");
  const [filterOpenOnly, setFilterOpenOnly] = useState(true);
  const [order, setOrder] = useState<TaskOrder>(params.get("order") === "due" ? "due" : "newest");
  const [dueFilter, setDueFilter] = useState<TaskDueFilter>(
    params.get("due") === "overdue" ? "overdue" : params.get("due") === "dated" ? "dated" : "any",
  );
  const [taskSearch, setTaskSearch] = useState("");
  const [inboxSearch, setInboxSearch] = useState("");
  const [taskOffset, setTaskOffset] = useState(0);
  const [inboxOffset, setInboxOffset] = useState(0);

  const [tasks, setTasks] = useState<Task[]>([]);
  // Review finding E: the server's count of the acts (TaskList.actionable), which the tab
  // shows; the page reads at most 100 rows, so counting them would under-tell.
  const [serverActionable, setServerActionable] = useState<number | null>(null);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksFailed, setTasksFailed] = useState(false);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxFailed, setInboxFailed] = useState(false);

  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);

  const loadTasks = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setTasksLoading(true);
      setTasksFailed(false);
      try {
        const body = await listTasks(token, {
          openOnly: filterOpenOnly,
          limit: 100,
          record,
          signal,
        });
        setTasks([...body.items]);
        setServerActionable(body.actionable ?? null);
      } catch (error: unknown) {
        if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
          return;
        }
        setTasksFailed(true);
      } finally {
        setTasksLoading(false);
      }
    },
    [token, filterOpenOnly, record],
  );

  const loadNotifications = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setInboxLoading(true);
      setInboxFailed(false);
      try {
        const body = await listNotifications(token, {
          limit: 100,
          signal,
        });
        setNotifications([...body.items]);
        setUnreadCount(body.unread);
      } catch (error: unknown) {
        if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
          return;
        }
        setInboxFailed(true);
      } finally {
        setInboxLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void loadTasks(controller.signal);
      void loadNotifications(controller.signal);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [loadTasks, loadNotifications]);

  async function handleCompleteTask(taskId: string): Promise<void> {
    if (busyTaskId !== null) {
      return;
    }
    setBusyTaskId(taskId);
    try {
      const result = await completeTask(token, taskId);
      setTasks((prev) =>
        prev.map((item) =>
          item.id === taskId
            ? { ...item, completedAt: result.completedAt ?? new Date().toISOString() }
            : item,
        ),
      );
      setToast({ message: strings.tasks.completed, tone: "info" });
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 409) {
        // The defensive path (wave W1): the server says this task closes only through
        // its act. Say so in its own plain words and offer the action instead.
        setTasks((prev) =>
          prev.map((item) =>
            item.id === taskId
              ? { ...item, completesThroughAction: true, actionableByMe: true }
              : item,
          ),
        );
        setToast({
          message: error.problem?.detail ?? strings.tasks.closesThroughAction,
          tone: "info",
        });
        return;
      }
      const message =
        error instanceof ApiError && error.problem?.detail
          ? error.problem.detail
          : strings.tasks.completeFailed;
      setToast({ message, tone: "danger" });
    } finally {
      setBusyTaskId(null);
    }
  }

  async function handleMarkRead(notificationItem: Notification): Promise<void> {
    // An attended row (its task is done) is not unread: nothing to mark, nothing to count.
    if (!isUnread(notificationItem)) {
      return;
    }
    try {
      const row = await markNotificationRead(token, notificationItem.id);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, readAt: row.readAt ?? new Date().toISOString() } : item,
        ),
      );
      setUnreadCount((count) => Math.max(0, count - 1));
    } catch {
      // The row stays unread on screen if read failed
    }
  }

  async function handleMarkAllNotifications(): Promise<void> {
    const pending = notifications.filter(isUnread);
    for (const item of pending) {
      await handleMarkRead(item);
    }
  }

  function handleOpenNotification(item: Notification): void {
    void handleMarkRead(item);
    const link = resolveNotificationLink(item);
    if (link !== null) {
      void navigate(link.path);
    }
  }

  const filteredTasks = useMemo(() => {
    const term = taskSearch.trim().toLowerCase();
    const kept = tasks.filter((item) => {
      if (filterOpenOnly && !isTaskOpen(item)) {
        return false;
      }
      if (!keepsDue(item, dueFilter)) {
        return false;
      }
      if (term.length === 0) {
        return true;
      }
      return (
        item.message.toLowerCase().includes(term) ||
        item.kind.toLowerCase().includes(term) ||
        item.subjectType.toLowerCase().includes(term)
      );
    });
    return orderTasks(kept, order);
  }, [tasks, filterOpenOnly, taskSearch, dueFilter, order]);
  // The inbox row of a handoff shows its task's due time when the task is loaded.
  const taskById = useMemo(() => new Map(tasks.map((item) => [item.id, item])), [tasks]);

  // Wave Experience A, item 3: with "Open only" the register is what waits on me; the
  // notes for my information sit beneath it, collapsed. "All tasks" is the history: every
  // row, done ones included.
  const registerTasks = useMemo(
    () => (filterOpenOnly ? filteredTasks.filter((item) => item.actionableByMe) : filteredTasks),
    [filteredTasks, filterOpenOnly],
  );
  const fyiTasks = useMemo(
    () => (filterOpenOnly ? filteredTasks.filter((item) => !item.actionableByMe) : []),
    [filteredTasks, filterOpenOnly],
  );
  const actionableCount = useMemo(() => {
    const read = tasks.filter((item) => item.actionableByMe && item.completedAt === null).length;
    return Math.max(read, serverActionable ?? read);
  }, [tasks, serverActionable]);

  const pagedTasks = useMemo(() => {
    return registerTasks.slice(taskOffset, taskOffset + PAGE_SIZE);
  }, [registerTasks, taskOffset]);

  const filteredNotifications = useMemo(() => {
    const term = inboxSearch.trim().toLowerCase();
    if (term.length === 0) {
      return notifications;
    }
    return notifications.filter((item) => item.message.toLowerCase().includes(term));
  }, [notifications, inboxSearch]);

  const pagedNotifications = useMemo(() => {
    return filteredNotifications.slice(inboxOffset, inboxOffset + PAGE_SIZE);
  }, [filteredNotifications, inboxOffset]);

  return (
    <div className="q-tasks-page" data-testid="tasks-page">
      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      <Breadcrumbs
        trail={[{ label: strings.nav.home, to: "/" }, { label: strings.tasks.heading }]}
      />

      <h2 className="q-shell__heading">{strings.tasks.heading}</h2>
      <p className="q-shell__lede">{strings.tasks.lede}</p>

      {record !== null && (
        <div
          className="q-ui mb-(--q-s1) flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-gold-soft bg-gold-wash px-(--q-s0) py-(--q-s-1)"
          role="status"
          data-testid="record-tasks-banner"
        >
          <ListFilter aria-hidden="true" className="size-4 shrink-0 text-gold" />
          <p className="min-w-0 flex-1 text-sm text-foreground">
            <strong>{strings.tasks.recordBanner(recordLabel)}</strong>{" "}
            <span className="text-muted-foreground">{strings.tasks.recordBannerLede}</span>
          </p>
          <Link to="/tasks" className="q-link text-sm" data-testid="record-tasks-show-all">
            {strings.tasks.recordShowAll}
          </Link>
        </div>
      )}

      <div className="q-actions-row" role="tablist" aria-label="Tasks and Inbox views">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "tasks"}
          className={`q-button${tab === "tasks" ? " q-mini--primary" : " q-button--quiet"}`}
          data-testid="tab-tasks"
          onClick={() => {
            setTab("tasks");
          }}
        >
          Tasks {actionableCount > 0 ? `(${String(actionableCount)})` : ""}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "inbox"}
          className={`q-button${tab === "inbox" ? " q-mini--primary" : " q-button--quiet"}`}
          data-testid="tab-inbox"
          onClick={() => {
            setTab("inbox");
          }}
        >
          Inbox {unreadCount > 0 ? `(${String(unreadCount)})` : ""}
        </button>
      </div>

      {tab === "tasks" && (
        <div data-testid="tasks-view-container">
          <RegisterView
            toolbar={
              <div
                className="q-actions-row"
                style={{ justifyContent: "space-between", marginBlock: "1rem" }}
              >
                <PageSearch
                  id="task-search-input"
                  value={taskSearch}
                  onChange={(val) => {
                    setTaskSearch(val);
                    setTaskOffset(0);
                  }}
                />
                <div className="q-actions-row">
                  <button
                    type="button"
                    className={`q-button${filterOpenOnly ? " q-mini--primary" : " q-button--quiet"}`}
                    data-testid="tasks-filter-open"
                    onClick={() => {
                      setFilterOpenOnly(true);
                      setTaskOffset(0);
                    }}
                  >
                    Open only
                  </button>
                  <button
                    type="button"
                    className={`q-button${!filterOpenOnly ? " q-mini--primary" : " q-button--quiet"}`}
                    data-testid="tasks-filter-all"
                    onClick={() => {
                      setFilterOpenOnly(false);
                      setTaskOffset(0);
                    }}
                  >
                    All tasks
                  </button>
                </div>
                <div
                  className="q-actions-row"
                  role="group"
                  aria-label={strings.tasks.orderLabel}
                  data-testid="tasks-order"
                >
                  {(["newest", "due"] as const).map((one) => (
                    <button
                      key={one}
                      type="button"
                      aria-pressed={order === one}
                      className={`q-button${order === one ? " q-mini--primary" : " q-button--quiet"}`}
                      data-testid={`tasks-order-${one}`}
                      onClick={() => {
                        setOrder(one);
                        setTaskOffset(0);
                      }}
                    >
                      {one === "newest" ? strings.tasks.orderNewest : strings.tasks.orderDue}
                    </button>
                  ))}
                </div>
                <div
                  className="q-actions-row"
                  role="group"
                  aria-label={strings.tasks.dueFilterLabel}
                  data-testid="tasks-due-filter"
                >
                  {(["any", "overdue", "dated"] as const).map((one) => (
                    <button
                      key={one}
                      type="button"
                      aria-pressed={dueFilter === one}
                      className={`q-button${dueFilter === one ? " q-mini--primary" : " q-button--quiet"}`}
                      data-testid={`tasks-due-${one}`}
                      onClick={() => {
                        setDueFilter(one);
                        setTaskOffset(0);
                      }}
                    >
                      {one === "any"
                        ? strings.tasks.dueFilterAny
                        : one === "overdue"
                          ? strings.tasks.dueFilterOverdue
                          : strings.tasks.dueFilterDated}
                    </button>
                  ))}
                </div>
              </div>
            }
            columns={
              <tr>
                {/* Wave W1: the action sits in the task's own cell, under its name, so on
                    a phone it is in view without scrolling the register sideways. */}
                <th scope="col">Task</th>
                <th scope="col">Context</th>
                <th scope="col">Entity</th>
                <th scope="col">{strings.tasks.dueColumn}</th>
                <th scope="col">Age</th>
                <th scope="col">Status</th>
              </tr>
            }
            loading={tasksLoading}
            failed={tasksFailed}
            empty={registerTasks.length === 0}
            emptyTitle={
              dueFilter !== "any"
                ? strings.tasks.dueEmptyTitle
                : filterOpenOnly
                  ? "No open tasks"
                  : "No tasks found"
            }
            emptyBody={
              dueFilter !== "any"
                ? strings.tasks.dueEmptyBody
                : "When tasks are assigned to you or your organisation, they appear here."
            }
            testId="tasks-table"
            pagination={
              registerTasks.length > PAGE_SIZE ? (
                <Pagination
                  total={registerTasks.length}
                  limit={PAGE_SIZE}
                  offset={taskOffset}
                  onMove={setTaskOffset}
                />
              ) : null
            }
          >
            {pagedTasks.map((task) => {
              const entity = resolveEntityLink(task);
              const open = isTaskOpen(task);
              const busy = busyTaskId === task.id;
              return (
                <tr
                  key={task.id}
                  data-testid="task-row"
                  data-overdue={isTaskOverdue(task) ? "true" : "false"}
                >
                  <td>
                    <div style={{ display: "grid", gap: "0.5rem", justifyItems: "start" }}>
                      <strong className="q-register__wrap">{taskTitle(task.kind)}</strong>
                      {open && task.completesThroughAction && (
                        <Link
                          to={taskActionPath(task)}
                          className="q-button q-mini--primary"
                          data-testid="open-action-link"
                        >
                          {strings.tasks.openAction}
                        </Link>
                      )}
                      {open && !task.completesThroughAction && (
                        <button
                          type="button"
                          className="q-button q-mini--primary"
                          data-testid="complete-task-button"
                          disabled={busy}
                          onClick={() => {
                            void handleCompleteTask(task.id);
                          }}
                        >
                          {busy ? strings.tasks.completing : strings.tasks.complete}
                        </button>
                      )}
                      {!open && <span className="q-note">{strings.tasks.done}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="q-register__wrap">
                      <p style={{ margin: 0 }}>{task.message}</p>
                      {strings.tasks.stations[task.kind] !== undefined && (
                        <span
                          className="q-note"
                          style={{ display: "block", marginTop: "0.25rem" }}
                          data-testid="task-station"
                        >
                          {strings.tasks.stations[task.kind]}
                        </span>
                      )}
                      {task.addressedToParty && (
                        <span className="q-note" style={{ display: "block", marginTop: "0.25rem" }}>
                          {strings.tasks.addressedToParty}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <Link to={entity.path} className="q-link" data-testid="task-entity-link">
                      {entity.label}
                    </Link>
                  </td>
                  <td>
                    <TaskDue task={task} showNone />
                  </td>
                  <td>
                    <span className="q-register__mono" title={day(task.createdAt)}>
                      {formatTaskAge(task.createdAt)}
                    </span>
                  </td>
                  <td>
                    <StateChip
                      state={open ? "OPEN" : "CLOSED"}
                      label={open ? "Open" : "Completed"}
                    />
                  </td>
                </tr>
              );
            })}
          </RegisterView>
          <FyiBlock count={fyiTasks.length} testId="tasks-fyi">
            <ul className="flex flex-col gap-2" data-testid="tasks-fyi-list">
              {fyiTasks.map((task) => (
                <li
                  key={task.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"
                  data-testid="tasks-fyi-row"
                >
                  <span className="min-w-[14rem] flex-1">
                    <strong>{taskTitle(task.kind)}</strong> {task.message}
                  </span>
                  <TaskDue task={task} />
                  <Link to={resolveEntityLink(task).path} className="q-link">
                    {resolveEntityLink(task).label}
                  </Link>
                  <button
                    type="button"
                    className="q-button q-button--quiet"
                    data-testid="fyi-complete-task-button"
                    disabled={busyTaskId === task.id}
                    onClick={() => {
                      void handleCompleteTask(task.id);
                    }}
                  >
                    {busyTaskId === task.id ? strings.tasks.completing : strings.tasks.complete}
                  </button>
                </li>
              ))}
            </ul>
          </FyiBlock>
        </div>
      )}

      {tab === "inbox" && (
        <div data-testid="inbox-view-container">
          <RegisterView
            toolbar={
              <div
                className="q-actions-row"
                style={{ justifyContent: "space-between", marginBlock: "1rem" }}
              >
                <PageSearch
                  id="inbox-search-input"
                  value={inboxSearch}
                  onChange={(val) => {
                    setInboxSearch(val);
                    setInboxOffset(0);
                  }}
                />
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="q-button"
                    data-testid="inbox-mark-all"
                    onClick={() => {
                      void handleMarkAllNotifications();
                    }}
                  >
                    {strings.shellUi.notificationsMarkAll}
                  </button>
                )}
              </div>
            }
            columns={
              <tr>
                <th scope="col">Message</th>
                <th scope="col">Entity</th>
                <th scope="col">{strings.tasks.dueColumn}</th>
                <th scope="col">Received</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            }
            loading={inboxLoading}
            failed={inboxFailed}
            empty={filteredNotifications.length === 0}
            emptyTitle={strings.shellUi.notificationsEmpty}
            emptyBody="Notifications about project handoffs and assignments appear here."
            testId="inbox-table"
            pagination={
              filteredNotifications.length > PAGE_SIZE ? (
                <Pagination
                  total={filteredNotifications.length}
                  limit={PAGE_SIZE}
                  offset={inboxOffset}
                  onMove={setInboxOffset}
                />
              ) : null
            }
          >
            {pagedNotifications.map((item) => {
              const link = resolveNotificationLink(item);
              const unread = isUnread(item);
              const attended = isAttended(item);
              const handoff = item.taskId === null ? undefined : taskById.get(item.taskId);
              return (
                <tr key={item.id} data-testid="notification-row">
                  <td>
                    <div className="q-register__wrap">
                      <span style={{ fontWeight: unread ? 600 : 400 }}>{item.message}</span>
                    </div>
                  </td>
                  <td>
                    {link !== null ? (
                      <button
                        type="button"
                        className="q-button q-button--quiet"
                        style={{ padding: 0 }}
                        data-testid="notification-open-link"
                        onClick={() => {
                          handleOpenNotification(item);
                        }}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <span className="q-note">-</span>
                    )}
                  </td>
                  <td>
                    {handoff !== undefined && isTaskOpen(handoff) ? (
                      <TaskDue task={handoff} />
                    ) : (
                      <span className="q-note">-</span>
                    )}
                  </td>
                  <td>
                    <span className="q-register__mono" title={day(item.createdAt)}>
                      {day(item.createdAt)}
                    </span>
                  </td>
                  <td>
                    {/* Wave W2: a handoff whose task is complete reads Done, whether or not
                        it was ever clicked; one whose task waits says so. */}
                    <StateChip
                      state={
                        attended
                          ? "CLOSED"
                          : unread || item.taskOpen
                            ? "AWAITING_RESPONSE"
                            : "CLOSED"
                      }
                      label={
                        attended
                          ? strings.shellUi.notificationsDoneOn(day(item.attendedAt))
                          : item.taskOpen
                            ? strings.shellUi.notificationsWaiting
                            : unread
                              ? "Unread"
                              : "Read"
                      }
                    />
                  </td>
                  <td>
                    {unread && (
                      <button
                        type="button"
                        className="q-button q-button--quiet"
                        data-testid="notification-mark-read"
                        onClick={() => {
                          void handleMarkRead(item);
                        }}
                      >
                        Mark read
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </RegisterView>
        </div>
      )}
    </div>
  );
}
